import React from 'react';
import PropTypes from 'prop-types';
import { getBaseButtonClasses, getFocusRingClasses, getIconSpacingClasses } from './buttonUtils';
import { brandColors, textColors, surfaces } from '../../tokens/colors';

/**
 * GhostButton Component
 * 
 * A transparent button with minimal styling that shows background on hover.
 * Used for tertiary actions, navigation links, and subtle interactions.
 * 
 * @example
 * // Basic ghost button
 * <GhostButton onClick={handleClick}>
 *   Learn More
 * </GhostButton>
 * 
 * @example
 * // With icon
 * <GhostButton leftIcon={<InfoIcon />}>
 *   Help
 * </GhostButton>
 * 
 * @example
 * // Primary color variant (blue)
 * <GhostButton variant="primary">
 *   Login
 * </GhostButton>
 */
const GhostButton = ({
  children,
  onClick,
  type = 'button',
  variant = 'neutral',
  size = 'md',
  disabled = false,
  fullWidth = false,
  leftIcon = null,
  rightIcon = null,
  className = '',
  ...rest
}) => {
  const baseClasses = getBaseButtonClasses({ size, disabled, fullWidth, loading: false });
  const focusRingClasses = getFocusRingClasses('ghost');

  // Variant-specific classes (theme-aware)
  const variantClasses = {
    neutral: [
      textColors.secondary,
      textColors.hover,
      surfaces.hover,
    ],
    primary: [
      brandColors.primary,
      brandColors.primaryHover,
      brandColors.primaryLight,
    ],
    danger: [
      'text-red-600 dark:text-red-400',
      'hover:text-red-800 dark:hover:text-red-300',
      'hover:bg-red-50 dark:hover:bg-red-900/20',
    ],
  };

  const classes = [
    baseClasses,
    ...(variantClasses[variant] || variantClasses.neutral),
    focusRingClasses,
    className,
  ].join(' ');

  const buttonContent = (
    <>
      {leftIcon && (
        <span className={getIconSpacingClasses('left', !!children)}>
          {leftIcon}
        </span>
      )}
      {children}
      {rightIcon && (
        <span className={getIconSpacingClasses('right', !!children)}>
          {rightIcon}
        </span>
      )}
    </>
  );

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={classes}
      {...rest}
    >
      {buttonContent}
    </button>
  );
};

GhostButton.propTypes = {
  /** Button content */
  children: PropTypes.node,
  /** Click handler */
  onClick: PropTypes.func,
  /** Button type */
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  /** Visual variant */
  variant: PropTypes.oneOf(['neutral', 'primary', 'danger']),
  /** Button size */
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  /** Disabled state */
  disabled: PropTypes.bool,
  /** Full width button */
  fullWidth: PropTypes.bool,
  /** Icon to display on the left */
  leftIcon: PropTypes.node,
  /** Icon to display on the right */
  rightIcon: PropTypes.node,
  /** Additional CSS classes */
  className: PropTypes.string,
};

export default GhostButton;
